import './entity-select.scss'

import { first } from 'rxjs/operators'
import { defineComponent, PropType } from 'vue'
import { BModal } from 'bootstrap-vue'

import { SearchTitleOptions, getTranslatedString, ReferenceEntityFieldType } from '@skinsoft/types'
import { DataStore } from '@skinsoft/data-store'
import { DATASTORES_FROM_REFERENCE_ENTITY_TYPE } from '@skinsoft/entity'
import { reactiveVueMixin } from '@skinsoft/reactive-vue'

const DEFAULT_PAGE_SIZE = 5

export const EntitySelect = defineComponent({
    mixins: [reactiveVueMixin],
    props: {
        type: {
            type: String as PropType<ReferenceEntityFieldType>
        },
        modalTitle: {},
        modalPlaceholder: {},
        startValue: { type: String },
        readonly: {},
        thesaurusIdentifier: { type: String },
        filter: { type: String },
        esPath: { type: String },
        bState: {},
        pageSize: {
            type: Number,
            default: DEFAULT_PAGE_SIZE,
        },
        dataCy: {
            type: String
        },
    },
    template: require('./entity-select.html'),
    data() {
        return {
            filterText: "",
            currentPageNumber: 1,
            nbPages: 1,
            titles: [] as any[],
            totalSize: 0,
            selectedTitle: { title: "" } as any,
            dataStore: undefined as DataStore<any, any> | undefined,
            isDestroyed: false,
        }
    },
    methods: {
        async subscribe() {
            if (this.dataStore) {
                this.unsubscribeFrom('entities-subscription')

                // Extra parameters when used to display thesaurus data or collaborator filtering
                let defaultFilter = ""
                let esPath = ""
                if (this.filter && this.esPath) {
                    defaultFilter = this.filter;
                    esPath = this.esPath;
                }

                if (this.thesaurusIdentifier) {
                    const thesaurus = await this.$thesaurusDataStore.fetchOneByIdentifier(this.thesaurusIdentifier)
                        .pipe(first()).toPromise()
                    // Unlike the RxJS subscriptions below (torn down on unmount by the mixin's pool),
                    // this raw awaited promise has no such guard, so it could resume on a destroyed
                    // instance if the field gets removed while the fetch is in flight.
                    if (this.isDestroyed) return
                    defaultFilter = thesaurus.id;
                    esPath = "thesaurusTermSkin.thesaurus.ref";
                }

                const searchOptions: SearchTitleOptions = {
                    filterText: this.filterText,
                    pageNumber: this.currentPageNumber - 1,
                    defaultFilter,
                    size: this.pageSize,
                    esPath,
                    language: this.getAppLocale(),
                    isForUnimarc: false,
                }

                this.subscribeTo(
                    this.dataStore.searchTitles(searchOptions)
                        .pipe(
                            first(),
                        )
                    , (
                        data => {
                            this.titles = data.results.map(
                                title => {
                                    return (
                                        {
                                            id: title.id,
                                            text: getTranslatedString(title.genericEntityTitle, this.getAppLocale()),
                                        })
                                }
                            )
                            this.totalSize = data.totalSize;
                            const extraPage = data.totalSize % this.pageSize > 0 ? 1 : 0;
                            this.nbPages = Math.floor(data.totalSize / this.pageSize) + extraPage;
                        }
                    )
                    ,
                    'entities-subscription')
            }
        },
        openModal() {
            (this.$refs.modal as BModal).show()
        },
        validateSelection() {
            this.$emit('entity-selected', this.selectedTitle)
            ;(this.$refs.modal as BModal).hide()
        },
        decrementPageNumber() {
            const newPage = this.currentPageNumber - 1;
            if (newPage >= 1) {
                this.currentPageNumber = newPage;
            }
        },
        incrementPageNumber() {
            const newPage = this.currentPageNumber + 1;
            if (newPage <= this.nbPages) {
                this.currentPageNumber = newPage;
            }
        },
        onModalClose() {
            this.subscribeToStartValue();
            (this.$refs.modal as BModal).hide()
        },
        subscribeToStartValue() {
            if (this.dataStore) {
                this.unsubscribeFrom('start-value-subscription')
                this.subscribeTo(
                    this.dataStore.searchTitle(
                        this.startValue ?? '',
                        undefined,
                        this.getAppLocale(),
                    )
                        .pipe(first())
                    ,
                    data => {
                        if (data.totalSize > 0) {
                            this.selectedTitle = { text: getTranslatedString(data.results[0].genericEntityTitle, this.getAppLocale()) }
                        }
                    }
                    ,
                    'start-value-subscription'
                )
            }
        },
    },
    watch: {
        filterText: {
            handler() {
                this.subscribe()
            },
            immediate: true,
            deep: true,
        },
        currentPageNumber() {
            this.subscribe();
        },
        startValue: {
            handler() {
                if (this.startValue) {
                    this.subscribeToStartValue()
                }
            },
            immediate: true,
        },
        type: {
            handler() {
                // Retrieving the right datastore adapted to the entity type
                const dataStore = this.type ? DATASTORES_FROM_REFERENCE_ENTITY_TYPE[this.type] : undefined
                if(dataStore) {
                    this.dataStore = dataStore.getInstance();
                } else {
                    // LOG
                    throw new Error()
                }

                if (this.startValue) {
                    this.subscribeToStartValue()
                }
                this.subscribe();
            },
            immediate: true,
        },
    },
    beforeUnmount() {
        this.isDestroyed = true
    },
})
